import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useAlertManager } from '../../components/AlertProvider';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import { apiService } from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';
import './RegisterDriverScreen.css';

interface VehicleBrand {
  id: number;
  name: string;
  logo_url?: string;
  models: VehicleModel[];
}

interface VehicleModel {
  id: number;
  name: string;
}

const RegisterDriverScreen: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+994',
    password: '',
    password_confirmation: '',
    license_number: '',
    license_expiry: '',
    date_of_birth: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    vehicle_type_id: '',
    plate_number: '',
    brand_id: '',
    model_id: '',
    model: '', 
    year: '',
    color: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [brands, setBrands] = useState<VehicleBrand[]>([]);
  const [, setLoadingBrands] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlertManager();
  const { registerDriver } = useAuthStore();

  useEffect(() => {
    loadVehicleBrands();
  }, []);

  const loadVehicleBrands = async () => {
    setLoadingBrands(true);
    try {
      const response: any = await apiService.get(API_ENDPOINTS.VEHICLE_BRANDS.LIST);
      setBrands(response || []);
    } catch (error) {
      console.error('Failed to load vehicle brands:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleBrandChange = (brandId: string) => {
    setFormData({
      ...formData,
      brand_id: brandId,
      model_id: '', 
    });
  };

  const selectedBrand = brands.find((b) => b.id.toString() === formData.brand_id);
  const availableModels = selectedBrand?.models || [];

  const handleRegister = async () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.password ||
      !formData.license_number ||
      !formData.date_of_birth ||
      !formData.address
    ) {
      showAlert('Error', 'Please fill in all required fields', [{ text: 'OK' }]);
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      showAlert('Error', 'Passwords do not match', [{ text: 'OK' }]);
      return;
    }

    if (formData.password.length < 8) {
      showAlert('Error', 'Password must be at least 8 characters', [{ text: 'OK' }]);
      return;
    }

    setLoading(true);
    try {
      const { password_confirmation, ...registrationData } = formData;


      const payload: any = { ...registrationData };


      if (payload.year && typeof payload.year === 'string') {
        const yearNum = parseInt(payload.year, 10);
        if (!isNaN(yearNum) && yearNum > 0) {
          payload.year = yearNum;
        } else {
          delete payload.year; 
        }
      }


      if (payload.brand_id) {
        const brandIdNum = parseInt(payload.brand_id as any, 10);
        if (!isNaN(brandIdNum) && brandIdNum > 0) {
          (payload as any).brand_id = brandIdNum;
        } else {
          delete (payload as any).brand_id;
        }
      } else {
        delete (payload as any).brand_id;
      }

      if (payload.model_id) {
        const modelIdNum = parseInt(payload.model_id as any, 10);
        if (!isNaN(modelIdNum) && modelIdNum > 0) {
          (payload as any).model_id = modelIdNum;

          if (selectedBrand) {
            const selectedModel = selectedBrand.models.find((m) => m.id.toString() === payload.model_id);
            if (selectedModel) {
              (payload as any).model = selectedModel.name;
            }
          }
        } else {
          delete (payload as any).model_id;
        }
      } else {
        delete (payload as any).model_id;

        if (!payload.model || payload.model.trim() === '') {
          delete payload.model;
        }
      }


      if (payload.vehicle_type_id && typeof payload.vehicle_type_id === 'string') {
        const vehicleTypeIdNum = parseInt(payload.vehicle_type_id, 10);
        if (!isNaN(vehicleTypeIdNum) && vehicleTypeIdNum > 0) {
          payload.vehicle_type_id = vehicleTypeIdNum;
        } else {
          delete payload.vehicle_type_id;
        }
      }


      if (import.meta.env.DEV) {
        console.log('📝 Registering driver with vehicle data:', {
          brand_id: payload.brand_id,
          model_id: payload.model_id,
          model: payload.model,
          plate_number: payload.plate_number,
          year: payload.year,
          color: payload.color,
          vehicle_type_id: payload.vehicle_type_id,
        });
      }

      await registerDriver(payload);
      navigate('/verify-phone');
    } catch (error: any) {
      showAlert('Registration Failed', error.message || 'Registration failed', [{ text: 'OK' }]);
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title: string, children: React.ReactNode) => (
    <div className="section">
      <h3 className="section-title">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="register-container">
      <div className="register-content">
        <div className="header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <MaterialCommunityIcons name="arrow-back" size={24} color={Colors.textPrimary} />
          </button>
        </div>

        <div className="content">
          <div className="icon-container">
            <div className="icon-circle">
              <MaterialCommunityIcons name="car" size={40} color={Colors.primary} />
            </div>
          </div>
          <h1 className="title">Driver Registration</h1>
          <p className="subtitle">Join as a driver and start earning</p>

          {renderSection(
            'Personal Information',
            <>
              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="account-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    autoComplete="name"
                    inputMode="text"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="email-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="email"
                    className="input-field"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
              </div>

              <div className="phone-row">
                <div className="input-wrapper country-code-wrapper">
                  <div className="input-container country-code-container">
                    <MaterialCommunityIcons
                      name="phone-outline"
                      size={22}
                      color={Colors.textSecondary}
                    />
                    <input
                      type="text"
                      className="input-field country-code-input"
                      placeholder="Code"
                      value={formData.country_code}
                      onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                      autoComplete="tel-country-code"
                      inputMode="tel"
                    />
                  </div>
                </div>
                <div className="input-wrapper phone-wrapper">
                  <div className="input-container">
                    <input
                      type="tel"
                      className="input-field"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </div>
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="calendar-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="date"
                    className="input-field"
                    placeholder="Date of Birth (YYYY-MM-DD)"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    autoComplete="bday"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="map-marker-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <textarea
                    className="input-field textarea-field"
                    placeholder="Address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={3}
                    autoComplete="street-address"
                    inputMode="text"
                  />
                </div>
              </div>
            </>
          )}

          {renderSection(
            'License Information',
            <>
              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="card-account-details-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="text"
                    className="input-field"
                    placeholder="License Number"
                    value={formData.license_number}
                    onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                    autoComplete="off"
                    inputMode="text"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="calendar-clock-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="date"
                    className="input-field"
                    placeholder="License Expiry (YYYY-MM-DD)"
                    value={formData.license_expiry}
                    onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                    autoComplete="off"
                  />
                </div>
              </div>
            </>
          )}

          {renderSection(
            'Emergency Contact',
            <>
              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="account-alert-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Emergency Contact Name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    autoComplete="name"
                    inputMode="text"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="phone-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="tel"
                    className="input-field"
                    placeholder="Emergency Contact Phone"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>
            </>
          )}

          {renderSection(
            'Vehicle Information (Optional)',
            <>
              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="car-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Plate Number"
                    value={formData.plate_number}
                    onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                    autoComplete="off"
                    inputMode="text"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="car-multiple"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <select
                    className="input-field"
                    value={formData.brand_id}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    style={{
                      appearance: 'none',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      color: Colors.text,
                      fontSize: '16px',
                      padding: '0',
                    }}
                  >
                    <option value="">Select Brand</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="car-sports"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <select
                    className="input-field"
                    value={formData.model_id}
                    onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                    disabled={!formData.brand_id || availableModels.length === 0}
                    style={{
                      appearance: 'none',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      color: formData.brand_id && availableModels.length > 0 ? Colors.text : Colors.textSecondary,
                      fontSize: '16px',
                      padding: '0',
                      opacity: formData.brand_id && availableModels.length > 0 ? 1 : 0.6,
                    }}
                  >
                    <option value="">Select Model</option>
                    {availableModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="phone-row">
                <div className="input-wrapper year-wrapper">
                  <div className="input-container">
                    <input
                      type="number"
                      className="input-field"
                      placeholder="Year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      autoComplete="off"
                      inputMode="numeric"
                      min="1900"
                      max="2100"
                    />
                  </div>
                </div>
                <div className="input-wrapper color-wrapper">
                  <div className="input-container">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      autoComplete="off"
                      inputMode="text"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {renderSection(
            'Password',
            <>
              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="lock-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field"
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="input-wrapper">
                <div className="input-container">
                  <MaterialCommunityIcons
                    name="lock-check-outline"
                    size={22}
                    color={Colors.textSecondary}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field"
                    placeholder="Confirm Password"
                    value={formData.password_confirmation}
                    onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </>
          )}

          <button
            className={`register-button ${loading ? 'disabled' : ''}`}
            onClick={handleRegister}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register as Driver'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterDriverScreen;

